import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Shield, RefreshCw, Download, AlertTriangle, CheckCircle2, Clock,
  TrendingUp, TrendingDown, Minus, Activity, Play, ChevronRight,
  XCircle, User, Filter, ArrowUpRight, ArrowDownRight, X
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
};

// ── Score Ring Component ──
const ScoreRing = ({ score, size = 140, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "#0E8A55" : score >= 60 ? "#D97706" : "#C7322A";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="rgba(255,255,255,0.1)" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-extrabold text-white tracking-tight">{score}</span>
        <span className="text-xs text-slate-400 font-medium">/ 100</span>
      </div>
    </div>
  );
};

// ── Framework Card ──
const FrameworkCard = ({ fw, data, onClick }) => {
  const hasGaps = data.fail > 0;
  const hasCritical = data.criticalGaps > 0;

  return (
    <motion.div variants={itemVariants} whileHover={{ y: -2 }} className="group cursor-pointer" onClick={onClick}>
      <Card className="relative overflow-hidden border border-slate-200/80 bg-white hover:shadow-lg transition-all duration-300" data-testid={`framework-card-${fw}`}>
        <div className="h-1" style={{ backgroundColor: data.color }} />
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{data.label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-extrabold text-slate-900">{data.score}</span>
                <span className="text-sm text-slate-400">/100</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {hasCritical && (
                <Badge variant="destructive" className="animate-pulse text-[10px] px-1.5 py-0.5">
                  CRITICAL
                </Badge>
              )}
              {hasGaps && !hasCritical && (
                <Badge variant="outline" className="border-red-300 text-red-600 text-[10px] px-1.5 py-0.5">
                  {data.fail + data.highGaps} Gaps
                </Badge>
              )}
              <div className="flex items-center gap-0.5 text-xs">
                {data.trend === "UP" ? (
                  <><ArrowUpRight className="w-3 h-3 text-emerald-600" /><span className="text-emerald-600">+{data.trendDelta}</span></>
                ) : data.trend === "DOWN" ? (
                  <><ArrowDownRight className="w-3 h-3 text-red-500" /><span className="text-red-500">{data.trendDelta}</span></>
                ) : (
                  <><Minus className="w-3 h-3 text-slate-400" /><span className="text-slate-400">0</span></>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 text-xs font-medium">
            <span className="text-emerald-600">{data.pass} Pass</span>
            {data.fail > 0 && <span className="text-red-500">{data.fail} Fail</span>}
            {data.warn > 0 && <span className="text-amber-500">{data.warn} Warn</span>}
          </div>
          <div className="mt-3">
            <Progress value={data.score} className="h-1.5" />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              {data.lastRun ? new Date(data.lastRun).toLocaleDateString() : "Not run"}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#FF7A59] transition-colors" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ── Severity Badge ──
const SeverityBadge = ({ severity }) => {
  const styles = {
    CRITICAL: "bg-red-100 text-red-800 border-red-200",
    HIGH: "bg-orange-100 text-orange-800 border-orange-200",
    MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
    LOW: "bg-blue-100 text-blue-800 border-blue-200",
    INFO: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[severity] || styles.INFO}`} data-testid={`severity-${severity?.toLowerCase()}`}>
      {severity}
    </span>
  );
};

// ── Main Compliance Dashboard Page ──
const ComplianceDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const [isRunning, setIsRunning] = useState(false);
  const [resolveModal, setResolveModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [detailPanel, setDetailPanel] = useState(null);
  const [findingsFilter, setFindingsFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [assignUserId, setAssignUserId] = useState("");

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get("/compliance-dashboard/dashboard");
      setData(res.data);
      setIsRunning(!!res.data.activeRun);
    } catch (err) {
      console.error("Failed to fetch compliance data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    api.get("/users").then(r => setUsers(r.data?.users || r.data || [])).catch(() => {});
  }, [fetchDashboard]);

  // Auto-refresh every 60s (5s if run active)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboard();
      setCountdown(isRunning ? 5 : 60);
    }, isRunning ? 5000 : 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard, isRunning]);

  // Countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? (isRunning ? 5 : 60) : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const handleRunAll = async () => {
    try {
      await api.post("/compliance-dashboard/run");
      setIsRunning(true);
      setCountdown(5);
      toast.success("Compliance test run started");
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start run");
    }
  };

  const handleResolve = async () => {
    if (!resolveModal || !resolutionNotes.trim()) return;
    try {
      await api.patch(`/compliance-dashboard/findings/${resolveModal.id}/resolve`, {
        resolution_notes: resolutionNotes,
      });
      toast.success("Finding marked as resolved");
      setResolveModal(null);
      setResolutionNotes("");
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to resolve finding");
    }
  };

  const handleAssign = async () => {
    if (!assignModal || !assignUserId) return;
    const selectedUser = users.find(u => u.id === assignUserId);
    try {
      await api.patch(`/compliance-dashboard/findings/${assignModal.id}/assign`, {
        assigned_to: assignUserId,
        assigned_name: selectedUser?.full_name || "Unknown",
      });
      toast.success("Finding assigned");
      setAssignModal(null);
      setAssignUserId("");
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to assign finding");
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/compliance-dashboard/findings/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `compliance_gaps_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  const scoreColor = data?.overallScore >= 80 ? "text-emerald-400" : data?.overallScore >= 60 ? "text-amber-400" : "text-red-400";
  const filteredFindings = data?.criticalFindings?.filter(f =>
    findingsFilter === "all" || f.status === findingsFilter
  ) || [];

  return (
    <AdminLayout>
      <motion.div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" variants={containerVariants} initial="hidden" animate="show">
        {/* ── Top Banner ── */}
        <motion.div variants={itemVariants} data-testid="compliance-banner">
          <Card className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-0 overflow-hidden">
            <CardContent className="p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
                <ScoreRing score={data?.overallScore || 0} />
                <div className="flex-1 text-center lg:text-left">
                  <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight">
                    Compliance Posture
                  </h1>
                  <p className="text-slate-400 text-sm mt-1">
                    Real-time security and compliance monitoring across 9 frameworks
                  </p>
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 mt-4">
                    <div className={`flex items-center gap-1 ${scoreColor}`}>
                      {data?.overallTrend === "UP" ? <TrendingUp className="w-4 h-4" /> :
                        data?.overallTrend === "DOWN" ? <TrendingDown className="w-4 h-4" /> :
                        <Minus className="w-4 h-4" />}
                      <span className="text-sm font-semibold">
                        {data?.overallTrendDelta > 0 ? "+" : ""}{data?.overallTrendDelta} pts from last run
                      </span>
                    </div>
                    <span className="text-slate-500 text-xs">
                      Updated: {data?.lastUpdated ? new Date(data.lastUpdated).toLocaleTimeString() : "N/A"}
                    </span>
                    <span className="text-slate-500 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Refresh in {countdown}s
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {isRunning && data?.activeRun ? (
                    <div className="bg-slate-800 rounded-xl p-4 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-4 h-4 text-[#FF7A59] animate-pulse" />
                        <span className="text-sm text-white font-semibold">Running...</span>
                      </div>
                      <Progress value={(data.activeRun.progress / data.activeRun.total) * 100} className="h-2" />
                      <p className="text-xs text-slate-400 mt-1">
                        {data.activeRun.progress} of {data.activeRun.total} checks
                      </p>
                    </div>
                  ) : (
                    <Button onClick={handleRunAll} className="bg-[#FF7A59] hover:bg-[#FF8A6C] text-white font-semibold gap-2" data-testid="run-all-tests-btn">
                      <Play className="w-4 h-4" /> Run All Tests
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ── Framework Score Cards (3x3 grid) ── */}
        <motion.div variants={itemVariants}>
          <h2 className="text-lg font-bold text-slate-900 mb-4" data-testid="frameworks-heading">Framework Scores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.frameworks && Object.entries(data.frameworks).map(([key, fw]) => (
              <FrameworkCard key={key} fw={key} data={fw} onClick={() => setDetailPanel(key)} />
            ))}
          </div>
        </motion.div>

        {/* ── Critical Findings Panel ── */}
        <motion.div variants={itemVariants} data-testid="critical-findings-panel">
          <Card className="border-red-200 bg-white">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <CardTitle className="text-lg text-slate-900">
                    Findings ({filteredFindings.length})
                  </CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Tabs value={findingsFilter} onValueChange={setFindingsFilter} className="h-8">
                    <TabsList className="h-8">
                      <TabsTrigger value="all" className="text-xs h-7 px-3">All</TabsTrigger>
                      <TabsTrigger value="open" className="text-xs h-7 px-3">Open</TabsTrigger>
                      <TabsTrigger value="in_progress" className="text-xs h-7 px-3">In Progress</TabsTrigger>
                      <TabsTrigger value="resolved" className="text-xs h-7 px-3">Resolved</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Button variant="outline" size="sm" onClick={handleExport} className="gap-1" data-testid="export-csv-btn">
                    <Download className="w-3.5 h-3.5" /> CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="findings-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Severity</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Framework</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Control</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Finding</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Days</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFindings.map((f) => (
                      <tr key={f.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`finding-row-${f.id}`}>
                        <td className="py-2.5 px-3"><SeverityBadge severity={f.severity} /></td>
                        <td className="py-2.5 px-3 font-medium text-slate-700">{f.framework}</td>
                        <td className="py-2.5 px-3">
                          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{f.control_id}</span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 max-w-xs truncate">{f.finding}</td>
                        <td className="py-2.5 px-3 text-slate-500">{f.days_open}d</td>
                        <td className="py-2.5 px-3">
                          <Badge variant={f.status === "resolved" ? "default" : f.status === "in_progress" ? "secondary" : "outline"}
                            className={f.status === "open" ? "border-red-200 text-red-600" : ""}>
                            {f.status === "in_progress" ? "In Progress" : f.status.charAt(0).toUpperCase() + f.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex gap-1">
                            {f.status !== "resolved" && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setResolveModal(f)} data-testid={`resolve-btn-${f.id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setAssignModal(f)} data-testid={`assign-btn-${f.id}`}>
                                  <User className="w-3.5 h-3.5 mr-1" /> Assign
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredFindings.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-400">
                          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                          No findings in this category
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ── 30-Day Trend Chart ── */}
        <motion.div variants={itemVariants} data-testid="trend-chart-section">
          <Card className="bg-white border border-slate-200/80">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-slate-900">30-Day Compliance Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data?.trendHistory || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }}
                      tickFormatter={v => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })} />
                    <YAxis domain={[40, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <RechartsTooltip
                      contentStyle={{ background: "#1e293b", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      labelFormatter={v => new Date(v).toLocaleDateString()}
                      formatter={(value, name) => [`${value}/100`, "Score"]}
                    />
                    <ReferenceLine y={80} stroke="#0E8A55" strokeDasharray="4 4" label={{ value: "Target (80)", fill: "#0E8A55", fontSize: 10 }} />
                    <ReferenceLine y={60} stroke="#C7322A" strokeDasharray="4 4" label={{ value: "Critical (60)", fill: "#C7322A", fontSize: 10 }} />
                    <Line type="monotone" dataKey="overallScore" stroke="#FF7A59" strokeWidth={2.5}
                      dot={{ fill: "#FF7A59", r: 3 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ── Gap Remediation Tracker (Table View) ── */}
        <motion.div variants={itemVariants} data-testid="gap-tracker">
          <Card className="bg-white border border-slate-200/80">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-slate-900">Gap Remediation Tracker</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {["open", "in_progress", "resolved"].map(status => {
                  const items = (data?.criticalFindings || []).filter(f => f.status === status);
                  const labels = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
                  const colors = { open: "border-red-300 bg-red-50/30", in_progress: "border-amber-300 bg-amber-50/30", resolved: "border-emerald-300 bg-emerald-50/30" };
                  return (
                    <div key={status} className={`border rounded-xl p-4 ${colors[status]}`} data-testid={`gap-column-${status}`}>
                      <h3 className="font-bold text-sm text-slate-700 mb-3">{labels[status]} ({items.length})</h3>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {items.map(f => (
                          <div key={f.id} className="bg-white rounded-lg p-3 border border-slate-100 shadow-sm">
                            <div className="flex items-center justify-between mb-1">
                              <SeverityBadge severity={f.severity} />
                              <span className="text-[10px] text-slate-400">{f.days_open}d</span>
                            </div>
                            <p className="text-xs font-semibold text-slate-700">{f.framework} - {f.control_id}</p>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{f.finding}</p>
                            {f.assigned_name && (
                              <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                <User className="w-2.5 h-2.5" /> {f.assigned_name}
                              </p>
                            )}
                          </div>
                        ))}
                        {items.length === 0 && (
                          <p className="text-xs text-slate-400 text-center py-4">No items</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* ── Resolve Modal ── */}
      <Dialog open={!!resolveModal} onOpenChange={() => setResolveModal(null)}>
        <DialogContent data-testid="resolve-modal">
          <DialogHeader>
            <DialogTitle>Resolve Finding</DialogTitle>
          </DialogHeader>
          {resolveModal && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-semibold">{resolveModal.framework} - {resolveModal.control_id}</p>
                <p className="text-xs text-slate-500 mt-1">{resolveModal.finding}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Resolution Notes</label>
                <Textarea value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)}
                  placeholder="Describe how this finding was resolved..." className="mt-1"
                  data-testid="resolution-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveModal(null)}>Cancel</Button>
            <Button onClick={handleResolve} disabled={!resolutionNotes.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-resolve-btn">
              Mark as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Assign Modal ── */}
      <Dialog open={!!assignModal} onOpenChange={() => setAssignModal(null)}>
        <DialogContent data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle>Assign to Engineer</DialogTitle>
          </DialogHeader>
          {assignModal && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-semibold">{assignModal.framework} - {assignModal.control_id}</p>
                <p className="text-xs text-slate-500 mt-1">{assignModal.finding}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Assign To</label>
                <Select value={assignUserId} onValueChange={setAssignUserId}>
                  <SelectTrigger className="mt-1" data-testid="assign-user-select">
                    <SelectValue placeholder="Select team member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role === "super_admin" || u.role === "tenant_admin").map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal(null)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!assignUserId}
              className="bg-[#FF7A59] hover:bg-[#FF8A6C] text-white" data-testid="confirm-assign-btn">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Framework Detail Slide-out ── */}
      <Dialog open={!!detailPanel} onOpenChange={() => setDetailPanel(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="framework-detail-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {detailPanel && data?.frameworks?.[detailPanel]?.label} — Detail
            </DialogTitle>
          </DialogHeader>
          {detailPanel && data?.frameworks?.[detailPanel] && (() => {
            const fw = data.frameworks[detailPanel];
            const fwFindings = (data.criticalFindings || []).filter(
              f => f.framework.toLowerCase() === detailPanel.toLowerCase() ||
                   f.framework.toLowerCase() === fw.label?.split(" ")[0]?.toLowerCase()
            );
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{fw.pass}</p>
                    <p className="text-xs text-emerald-600">Passed</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{fw.fail}</p>
                    <p className="text-xs text-red-600">Failed</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{fw.warn}</p>
                    <p className="text-xs text-amber-600">Warnings</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{fw.score}</p>
                    <p className="text-xs text-blue-600">Score</p>
                  </div>
                </div>
                {fwFindings.length > 0 ? (
                  <div>
                    <h3 className="font-bold text-sm text-slate-700 mb-2">Findings ({fwFindings.length})</h3>
                    <div className="space-y-2">
                      {fwFindings.map(f => (
                        <div key={f.id} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <SeverityBadge severity={f.severity} />
                            <span className="font-mono text-xs">{f.control_id}</span>
                          </div>
                          <p className="text-sm text-slate-700">{f.finding}</p>
                          <p className="text-xs text-slate-500 mt-1">Remediation: {f.remediation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                    <p className="text-sm">No open findings for this framework</p>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ComplianceDashboardPage;
